import type { Member } from '@stytch/nextjs/b2b'
import { ZodiosError } from '@zodios/core'
import { CLIENT_PAGES } from '@/constants/clientPortalPages'
import {
  applyPostAuthBase,
  initializeSelectedClientFromIdentity,
  syncPayloadPostClientSelection,
  type PostAuthDependencies,
  type SelectedClientInitializationResult,
} from '@/utilities/auth/authHelper'
import authRepository from '@/services/shared/authRepository'
import { clearOnboardingCookie } from '@/utilities/onboardingCookie'
import { withAnalyticsTracking } from '@/utilities/analytics/apiAnalytics'
import { errorAnalyticsPayload } from '../analytics/errorAnalytics'
import { USER_IDENTITY_SET_EVENT } from '@/utilities/client/userIdentityEvent'
import { getStytchJWT } from '@/utilities/stytch/getStytchJWT'
import {
  getAuthMethodFromStytchPayload,
  isImpersonatedStytchPayload,
} from '@/utilities/shared/stytchSession'
import { trackEvent } from '@/utilities/analytics/trackEvent'
import { PayloadRepository } from '@/services/payload/payloadRepository'
import type { IdentityInfoSchemaType } from '@/types/portal-api.generated'
import userRepository from '@/services/client/userRepository'
import { setPostSignupAuthUserIdBridge } from '@/utilities/auth/postSignupAuthUserIdBridge'
import { setCachedTrustedIdentityScopesFromIdentity } from '@/utilities/auth/trustedIdentityScopes'

type PostAuthDeps = PostAuthDependencies
type TrustedClientIdentityInitializationResult =
  | { kind: 'initialized'; scopes: IdentityInfoSchemaType['scopes']; selectedClientId: string }
  | Exclude<SelectedClientInitializationResult, { kind: 'initialized' }>

export type RunClientPostAuthOptions = {
  // Impersonation must only resume an existing Client Portal identity; pass `false` so a member
  // without one fails closed instead of silently provisioning a new CP user/client link.
  allowSignup?: boolean
}

export class ClientIdentityRequiredError extends Error {
  constructor() {
    super('No existing Client Portal identity was found and signup is not allowed for this flow.')
    this.name = 'ClientIdentityRequiredError'
  }
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (error instanceof ZodiosError) {
    return 'response' in error
      ? (error.response as { status?: number } | undefined)?.status
      : undefined
  }

  if (error && typeof error === 'object' && 'response' in error) {
    return (error as { response?: { status?: number } }).response?.status
  }

  return undefined
}

const isMissingClientIdentityError = (error: unknown): boolean => {
  const status = getErrorStatus(error)
  return status === 404 || status === 403 || status === 401
}

async function fetchClientIdentity(args: {
  eventName: string
  email: string
  orgSlug: string
  attempt?: number
  authMethod?: string
  authUserIdOverride?: string | number | null
}): Promise<IdentityInfoSchemaType | null> {
  const { eventName, email, orgSlug, attempt, authMethod, authUserIdOverride } = args

  try {
    const identity = await withAnalyticsTracking(
      () => authRepository.login({ authUserIdOverride }),
      {
        eventName,
        additionalProperties: {
          email,
          orgSlug,
          ...(attempt == null ? {} : { attempt }),
          ...(authMethod === undefined ? {} : { authMethod }),
        },
        getSuccessProperties: (result) => ({
          userId: result?.id,
          primaryClientId: result?.primary_client_id,
        }),
      },
    )
    return identity
  } catch (error) {
    if (isMissingClientIdentityError(error)) {
      return null
    }

    throw error
  }
}

async function initializeTrustedClientIdentity(args: {
  identity: IdentityInfoSchemaType
  member: Member
  preferredClientId?: string | null
  authUserIdOverride?: string | number | null
}): Promise<TrustedClientIdentityInitializationResult> {
  const { identity, member, preferredClientId, authUserIdOverride } = args
  const scopes = identity.scopes

  if (Object.keys(scopes).length === 0) {
    return { kind: 'unavailable', reason: 'missing_scopes' }
  }

  const isVerifiedAuthUser =
    authUserIdOverride != null && String(identity.id) === String(authUserIdOverride)

  const baseInitializationArgs = {
    scopes,
    getClientUser: ({ clientId }: { clientId: string }) =>
      userRepository.getClientUser({ clientId, authUserIdOverride }),
    identityEmail: member.email_address,
    organizationId: identity.organization_id,
    preferredClientId: preferredClientId ?? identity.primary_client_id,
  }

  const initializedClient = isVerifiedAuthUser
    ? await initializeSelectedClientFromIdentity({
        ...baseInitializationArgs,
        initializationMode: 'verified_auth_user',
        verifiedAuthUserId: authUserIdOverride,
      })
    : await initializeSelectedClientFromIdentity(baseInitializationArgs)

  if (initializedClient.kind !== 'initialized') {
    return initializedClient
  }

  return { kind: 'initialized', scopes, selectedClientId: initializedClient.selectedClientId }
}

type PayloadSyncFallbackReason =
  'no_identity' | 'empty_scopes' | 'no_selectable_partner' | 'client_user_failed' | 'email_mismatch'

function getPayloadSyncFallbackReason(
  identity: IdentityInfoSchemaType | null,
  initializedIdentity: TrustedClientIdentityInitializationResult,
): PayloadSyncFallbackReason {
  if (!identity?.id || initializedIdentity.kind === 'initialized') {
    return 'no_identity'
  }
  if (initializedIdentity.reason === 'missing_scopes') {
    return Object.keys(identity.scopes ?? {}).length === 0
      ? 'empty_scopes'
      : 'no_selectable_partner'
  }
  return initializedIdentity.reason
}

export async function runClientPostAuth(
  member: Member,
  orgSlug: string,
  deps: PostAuthDeps,
  options?: RunClientPostAuthOptions,
): Promise<string> {
  const { allowSignup = true } = options ?? {}
  applyPostAuthBase(member, orgSlug, deps)
  let syncedPayloadWithPartner = false
  let attemptedSignup = false
  try {
    let identity = await fetchClientIdentity({
      eventName: 'client:auth:check_post_auth_identity',
      email: member.email_address,
      orgSlug,
      authMethod: getAuthMethodFromStytchPayload(getStytchJWT()),
    })
    let initializedIdentity = identity?.id
      ? await initializeTrustedClientIdentity({
          identity,
          member,
          preferredClientId: identity.primary_client_id,
          authUserIdOverride: undefined,
        })
      : ({ kind: 'unavailable', reason: 'missing_scopes' } as const)

    if (identity?.id && initializedIdentity.kind === 'rejected') {
      identity = null
    }

    // If the user lacks an auth user ID, they're an invited user who needs to complete sign-up.
    if (!identity?.id) {
      if (!allowSignup) {
        throw new ClientIdentityRequiredError()
      }
      attemptedSignup = true
      const signUpResult = await withAnalyticsTracking(
        () => authRepository.signUpClient({ email: member.email_address }),
        {
          eventName: 'client:auth:sign_up_user',
          getSuccessProperties: (result) => ({
            clientId: result.client_id,
            email: result.email,
            userId: result.user_id,
          }),
        },
      )
      if (signUpResult?.user_id != null) {
        setPostSignupAuthUserIdBridge({
          memberId: member.member_id,
          authUserId: signUpResult.user_id,
        })
      }
      // The signup response gives us the new auth user ID, which is enough to scope the
      // immediate bootstrap requests and the first client-side requests after redirect while
      // the Stytch JWT is still missing internal_id. We still wait for trusted identity data
      // before choosing a partner, rather than guessing from signup metadata.
      identity = await fetchClientIdentity({
        eventName: 'client:auth:check_post_signup_identity',
        email: member.email_address,
        orgSlug,
        authUserIdOverride: signUpResult?.user_id,
      })

      if (
        identity?.id != null &&
        signUpResult?.user_id != null &&
        String(identity.id) !== String(signUpResult.user_id)
      ) {
        identity = null
      }

      initializedIdentity = identity?.id
        ? await initializeTrustedClientIdentity({
            identity,
            member,
            preferredClientId: signUpResult?.client_id ?? identity.primary_client_id,
            authUserIdOverride: signUpResult?.user_id,
          })
        : ({ kind: 'unavailable', reason: 'missing_scopes' } as const)
    }

    if (identity?.id && initializedIdentity.kind === 'initialized') {
      setCachedTrustedIdentityScopesFromIdentity(identity)
      await syncPayloadPostClientSelection({
        member,
        scopes: initializedIdentity.scopes,
        selectedClientId: initializedIdentity.selectedClientId,
      })
      syncedPayloadWithPartner = true
      // Re-identify user in analytics after sign-up to ensure identity is properly set
      // This is important because identifyUser was called before sign-up when identity wasn't available
      deps.identifyUser(member)
      // Dispatch event to notify components that user identity has been set
      // This allows components to refresh and read the updated identity from localStorage
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(USER_IDENTITY_SET_EVENT, { detail: { identity } }))
      }
    }
    if (!syncedPayloadWithPartner) {
      const reason = getPayloadSyncFallbackReason(identity, initializedIdentity)
      // allowSignup: false flows (impersonation) fail closed below, so a Payload token must not
      // be issued for a member whose Client Portal identity could not be initialized.
      trackEvent('client:auth:payload_sync_fallback', {
        reason,
        attemptedSignup,
        allowSignup,
        impersonated: isImpersonatedStytchPayload(getStytchJWT()),
        syncSkipped: !allowSignup,
      })
      if (allowSignup) {
        await PayloadRepository.syncUserToPayload({
          email: member.email_address,
          name: member.name,
          scopes: {},
        })
      }
    }
  } catch (error) {
    // Fail closed: signup is disallowed for this caller (e.g. impersonation), so the missing
    // identity must not be swallowed into a best-effort dashboard redirect below.
    if (error instanceof ClientIdentityRequiredError) {
      throw error
    }

    // Log errors for debugging, but don't block the user from accessing the dashboard.
    // While these errors don't prevent navigation, they can cause issues elsewhere in the app:
    // - No identity is set in localStorage, so authenticated API requests will fail (403/401)
    // - Client selection is not initialized, so users can't select or switch clients
    // - Components like NavbarTop will fail to load partners and show loading/error states
    // - The userIdentitySet event is not dispatched, so components won't refresh after signup
    // - Pages that depend on identity or client selection may not function properly
    //
    // Example scenario: If a user refreshes after Stytch sign-in completes but before the auth
    // signup request completes, they'll have a Stytch session but no backend identity, causing
    // all API requests to fail until they log out and back in.
    //
    // These errors are expected for new users during signup (e.g., 404/403 identity check
    // failures) but may indicate timing issues that affect the user experience.
    if (error instanceof ZodiosError) {
      const response =
        'response' in error
          ? (error.response as { status?: number; data?: unknown } | undefined)
          : undefined
      const status = response?.status
      // Only log non-expected errors (404/403 from login() calls are handled above and set identity = null)
      if (status !== 404 && status !== 403) {
        console.error('Error in runClientPostAuth:', {
          status,
          data: response?.data,
          email: member.email_address,
        })
      }
    } else {
      console.error('Unexpected error in runClientPostAuth:', errorAnalyticsPayload(error))
    }

    // Impersonation (and any other allowSignup: false caller) already cleared predecessor
    // partner/identity caches. Swallowing bootstrap failures would land them on the dashboard
    // with an impersonated Stytch session and no usable Client Portal identity.
    if (!allowSignup) {
      throw error
    }
  }

  if (!allowSignup && !syncedPayloadWithPartner) {
    throw new ClientIdentityRequiredError()
  }

  clearOnboardingCookie()
  return CLIENT_PAGES.dashboard
}
